<template>
  <div class="analytics-container">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h2><i class="bi bi-graph-up"></i> Analytics Dashboard</h2>
      <div class="d-flex gap-2">
        <select v-model="selectedDays" @change="loadData" class="form-select" style="width: auto;">
          <option value="7">Last 7 days</option>
          <option value="30">Last 30 days</option>
          <option value="90">Last 90 days</option>
        </select>
        <button @click="loadData" class="btn btn-outline-primary" :disabled="loading">
          <i class="bi bi-arrow-clockwise" :class="{ 'spinning': loading }"></i> Refresh
        </button>
      </div>
    </div>

    <!-- Overview Cards -->
    <div class="row mb-4" v-if="overview">
      <div class="col-md-3">
        <div class="card text-center">
          <div class="card-body">
            <h5 class="card-title text-primary">
              <i class="bi bi-eye"></i> Total Visits
            </h5>
            <h3 class="text-primary">{{ formatNumber(overview.total_visits) }}</h3>
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card text-center">
          <div class="card-body">
            <h5 class="card-title text-success">
              <i class="bi bi-people"></i> Unique Visitors
            </h5>
            <h3 class="text-success">{{ formatNumber(overview.total_unique_visitors) }}</h3>
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card text-center">
          <div class="card-body">
            <h5 class="card-title text-info">
              <i class="bi bi-window"></i> Sessions
            </h5>
            <h3 class="text-info">{{ formatNumber(overview.total_sessions) }}</h3>
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card text-center">
          <div class="card-body">
            <h5 class="card-title text-warning">
              <i class="bi bi-check-circle"></i> Success Rate
            </h5>
            <h3 class="text-warning">{{ overview.success_rate }}%</h3>
          </div>
        </div>
      </div>
    </div>

    <!-- Charts Row -->
    <div class="row mb-4">
      <!-- Daily Visits Chart -->
      <div class="col-lg-8">
        <div class="card">
          <div class="card-header">
            <h5 class="mb-0"><i class="bi bi-bar-chart"></i> Daily Visits</h5>
          </div>
          <div class="card-body">
            <canvas ref="dailyVisitsChart" height="300"></canvas>
          </div>
        </div>
      </div>

      <!-- Hourly Visits Chart -->
      <div class="col-lg-4">
        <div class="card">
          <div class="card-header">
            <h5 class="mb-0"><i class="bi bi-clock"></i> Last 24 Hours</h5>
          </div>
          <div class="card-body">
            <canvas ref="hourlyVisitsChart" height="300"></canvas>
          </div>
        </div>
      </div>
    </div>

    <!-- Most Visited Pages -->
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header">
            <h5 class="mb-0"><i class="bi bi-list-ol"></i> Most Visited Pages</h5>
          </div>
          <div class="card-body">
            <div v-if="loading" class="text-center py-4">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
            </div>
            <div v-else-if="mostVisitedPages.length === 0" class="text-center py-4 text-muted">
              <i class="bi bi-inbox display-4"></i>
              <p>No data available for the selected period</p>
            </div>
            <div v-else class="table-responsive">
              <table class="table table-hover">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Page URL</th>
                    <th>Visits</th>
                    <th>Unique Visitors</th>
                    <th>% of Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(page, index) in mostVisitedPages" :key="page.url">
                    <td>{{ index + 1 }}</td>
                    <td>
                      <code class="text-break">{{ page.url }}</code>
                    </td>
                    <td>
                      <span class="badge bg-primary">{{ formatNumber(page.visits) }}</span>
                    </td>
                    <td>
                      <span class="badge bg-success">{{ formatNumber(page.unique_visitors) }}</span>
                    </td>
                    <td>
                      <div class="progress" style="width: 100px; height: 20px; position: relative;">
                        <div class="progress-bar" :style="{ width: getPercentage(page.visits) + '%' }">
                        </div>
                        <span class="progress-text" :style="{ 
                          position: 'absolute', 
                          top: '50%', 
                          left: '50%', 
                          transform: 'translate(-50%, -50%)', 
                          fontSize: '11px',
                          fontWeight: 'bold',
                          color: '#000',
                          zIndex: 10
                        }">
                          {{ getPercentage(page.visits) }}%
                        </span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Chart, registerables } from 'chart.js'
import { ref, onMounted, nextTick } from 'vue'

Chart.register(...registerables)

export default {
  name: 'Analytics',
  setup() {
    const dailyVisitsChart = ref(null)
    const hourlyVisitsChart = ref(null)
    const selectedDays = ref(30)
    const loading = ref(false)
    const overview = ref(null)
    const mostVisitedPages = ref([])
    const dailyStats = ref([])
    const hourlyStats = ref([])
    const totalVisits = ref(0)

    let dailyChart = null
    let hourlyChart = null

    const loadData = async () => {
      loading.value = true
      try {
        await Promise.all([
          loadOverview(),
          loadDailyStats(),
          loadHourlyStats(),
          loadMostVisitedPages()
        ])
        await nextTick()
        updateCharts()
      } catch (error) {
        console.error('Failed to load analytics data:', error)
      } finally {
        loading.value = false
      }
    }

    const loadOverview = async () => {
      const response = await fetch(`/api/admin/analytics/overview?days=${selectedDays.value}`)
      const data = await response.json()
      if (data.success) {
        overview.value = data.data
      }
    }

    const loadDailyStats = async () => {
      const response = await fetch(`/api/admin/analytics/statistics?days=${selectedDays.value}`)
      const data = await response.json()
      if (data.success) {
        dailyStats.value = data.data
        totalVisits.value = data.data.reduce((sum, day) => sum + day.visits, 0)
      }
    }

    const loadHourlyStats = async () => {
      const response = await fetch('/api/admin/analytics/hourly')
      const data = await response.json()
      if (data.success) {
        hourlyStats.value = data.data
      }
    }

    const loadMostVisitedPages = async () => {
      const response = await fetch(`/api/admin/analytics/pages?days=${selectedDays.value}&limit=10`)
      const data = await response.json()
      if (data.success) {
        mostVisitedPages.value = data.data
      }
    }

    const updateCharts = () => {
      updateDailyChart()
      updateHourlyChart()
    }

    const updateDailyChart = () => {
      if (dailyChart) {
        dailyChart.destroy()
      }

      const ctx = dailyVisitsChart.value.getContext('2d')
      dailyChart = new Chart(ctx, {
        type: 'line',
        data: {
          labels: dailyStats.value.map(day => new Date(day.date).toLocaleDateString()),
          datasets: [
            {
              label: 'Visits',
              data: dailyStats.value.map(day => day.visits),
              borderColor: 'rgb(75, 192, 192)',
              backgroundColor: 'rgba(75, 192, 192, 0.2)',
              tension: 0.1
            },
            {
              label: 'Unique Visitors',
              data: dailyStats.value.map(day => day.unique_visitors),
              borderColor: 'rgb(255, 99, 132)',
              backgroundColor: 'rgba(255, 99, 132, 0.2)',
              tension: 0.1
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            y: {
              beginAtZero: true
            }
          },
          plugins: {
            legend: {
              position: 'top'
            }
          }
        }
      })
    }

    const updateHourlyChart = () => {
      if (hourlyChart) {
        hourlyChart.destroy()
      }

      const ctx = hourlyVisitsChart.value.getContext('2d')
      hourlyChart = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: hourlyStats.value.map(hour => hour.hour_display || hour.hour),
          datasets: [{
            label: 'Visits',
            data: hourlyStats.value.map(hour => hour.visits),
            backgroundColor: 'rgba(54, 162, 235, 0.6)',
            borderColor: 'rgba(54, 162, 235, 1)',
            borderWidth: 1
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            y: {
              beginAtZero: true
            },
            x: {
              title: {
                display: true,
                text: 'Time (Last 24 Hours)'
              }
            }
          },
          plugins: {
            legend: {
              display: false
            },
            title: {
              display: true,
              text: 'Visits in the Last 24 Hours'
            }
          }
        }
      })
    }

    const formatNumber = (num) => {
      return new Intl.NumberFormat().format(num)
    }

    const getPercentage = (visits) => {
      if (totalVisits.value === 0) return 0
      return Math.round((visits / totalVisits.value) * 100)
    }

    onMounted(() => {
      loadData()
    })

    return {
      dailyVisitsChart,
      hourlyVisitsChart,
      selectedDays,
      loading,
      overview,
      mostVisitedPages,
      loadData,
      formatNumber,
      getPercentage
    }
  }
}
</script>

<style scoped>
.analytics-container {
  padding: 20px 0;
}

.spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.card {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  border: 1px solid rgba(0, 0, 0, 0.125);
}

.card-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid rgba(0, 0, 0, 0.125);
}

.progress {
  background-color: #e9ecef;
}

.table th {
  border-top: none;
  font-weight: 600;
}

code {
  background-color: #f8f9fa;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 0.9em;
}
</style>

